import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  where,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Split a comma/newline separated string into a deduped list of trimmed emails.
 * @param {string} raw
 * @returns {string[]}
 */
function parseAudience(raw) {
  const parts = (raw || '')
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
  return Array.from(new Set(parts));
}

/**
 * Campaign builder — schedules an outbound email blast against a saved template.
 * Templates and campaigns are scoped to the active app.
 * @param {{ userData?: { companyId?: string }, activeAppId?: string, activeAppName?: string }} props
 */
export default function CampaignBuilder({ userData, activeAppId = '', activeAppName = '' }) {
  const { companyId } = useWorkspace();
  const resolvedCompanyId = companyId || userData?.companyId || null;

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');

  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [audienceRaw, setAudienceRaw] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Reset the selection whenever the app context changes.
    setTemplateId('');
    if (!resolvedCompanyId || !activeAppId) {
      setTemplates([]);
      return undefined;
    }
    let active = true;
    setTemplatesLoading(true);
    setTemplatesError('');
    // Filter by app only; sort client-side to avoid a composite index.
    getDocs(
      query(
        collection(db, 'companies', resolvedCompanyId, 'crm_templates'),
        where('appId', '==', activeAppId),
      ),
    )
      .then((snap) => {
        if (!active) return;
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort(
          (a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0),
        );
        setTemplates(rows);
      })
      .catch((e) => {
        if (active) {
          console.error('fetch crm templates:', e);
          setTemplatesError(e.message || 'Could not load templates.');
        }
      })
      .finally(() => {
        if (active) setTemplatesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resolvedCompanyId, activeAppId]);

  const audience = useMemo(() => parseAudience(audienceRaw), [audienceRaw]);
  const invalidEmails = useMemo(
    () => audience.filter((e) => !EMAIL_RE.test(e)),
    [audience],
  );

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    if (!resolvedCompanyId) {
      setError('No workspace is linked to your account.');
      return;
    }
    if (!activeAppId) {
      setError('Select an app context before scheduling a campaign.');
      return;
    }
    if (!name.trim()) {
      setError('Enter a campaign name.');
      return;
    }
    if (!templateId) {
      setError('Select a template for this campaign.');
      return;
    }
    if (audience.length === 0) {
      setError('Add at least one recipient email.');
      return;
    }
    if (invalidEmails.length > 0) {
      setError(`These addresses look invalid: ${invalidEmails.join(', ')}`);
      return;
    }
    if (!scheduledFor) {
      setError('Choose when this campaign should be delivered.');
      return;
    }
    const scheduledDate = new Date(scheduledFor);
    if (Number.isNaN(scheduledDate.getTime())) {
      setError('The scheduled delivery time is invalid.');
      return;
    }

    setSaving(true);
    try {
      const selected = templates.find((t) => t.id === templateId);
      const ref = await addDoc(
        collection(db, 'companies', resolvedCompanyId, 'crm_campaigns'),
        {
          appId: activeAppId,
          appName: activeAppName || null,
          name: name.trim(),
          templateId,
          templateName: selected?.name || '',
          audience,
          audienceCount: audience.length,
          scheduledFor: Timestamp.fromDate(scheduledDate),
          status: 'scheduled',
          createdAt: serverTimestamp(),
          createdByUid: auth.currentUser?.uid || null,
        },
      );
      setSuccess(
        `Campaign "${name.trim()}" scheduled for ${scheduledDate.toLocaleString()} `
        + `to ${audience.length} recipient${audience.length === 1 ? '' : 's'} (ID: ${ref.id}).`,
      );
      setName('');
      setTemplateId('');
      setAudienceRaw('');
      setScheduledFor('');
    } catch (e) {
      console.error('schedule crm campaign:', e);
      setError(e.message || 'Could not schedule campaign.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ maxWidth: 720 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          New campaign
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Schedule an outbound email blast for <strong>{activeAppName || '—'}</strong> using one of
          its saved templates.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        {templatesError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {templatesError}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            label="Campaign name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            placeholder="e.g. July product launch"
            helperText="Internal name for tracking."
          />

          <TextField
            select
            label="Template"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            fullWidth
            disabled={templatesLoading}
            helperText={
              templatesLoading
                ? 'Loading templates…'
                : templates.length === 0
                  ? 'No templates yet — create one in the Templates tab first.'
                  : 'Pick a saved email template.'
            }
            InputProps={
              templatesLoading
                ? { endAdornment: <CircularProgress size={18} sx={{ mr: 3 }} /> }
                : undefined
            }
          >
            {templates.length === 0 ? (
              <MenuItem value="" disabled>
                No templates available
              </MenuItem>
            ) : (
              templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name || '(untitled template)'}
                  {t.subject ? ` — ${t.subject}` : ''}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            label="Test audience"
            value={audienceRaw}
            onChange={(e) => setAudienceRaw(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            placeholder="jane@acme.com, john@acme.com"
            helperText={
              audience.length > 0
                ? `Enter comma-separated emails for this blast. ${audience.length} recipient${audience.length === 1 ? '' : 's'} detected.`
                : 'Enter comma-separated emails for this blast.'
            }
            error={invalidEmails.length > 0}
          />

          <TextField
            label="Schedule delivery"
            type="datetime-local"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="When the campaign should go out."
          />

          <Box>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <ScheduleSendIcon />}
              onClick={handleSubmit}
              disabled={saving || templatesLoading}
            >
              Schedule Campaign
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
